/**
 * DataForSEO site insights (AB#1035). Owners who save their own DataForSEO
 * login get plain-language status rows; without one, `configured` is false
 * and the backend makes no DataForSEO requests. The login is write-only —
 * responses only ever carry a masked hint.
 */

import { api } from './client';
import type { ApiResponse } from '../types';

export interface SeoInsightAction {
  kind: 'link';
  label: string;
  /** Admin route, e.g. `/sites/{slug}/posts/{id}`. */
  to: string;
}

export interface SeoInsight {
  id: string;
  ok: boolean;
  title: string;
  hint: string | null;
  /** 1 (nice to have) … 10 (fix first). */
  impact: number;
  action: SeoInsightAction | null;
}

export interface SeoInsightsResult {
  configured: boolean;
  /** Last successful DataForSEO fetch (ISO 8601). */
  fetched_at: string | null;
  /** Plain sentence, e.g. "Your DataForSEO key was rejected". Cached rows still come back. */
  error: string | null;
  rows: SeoInsight[];
  ai_citations: SeoInsight | null;
}

/** `account` (default) covers every site; `site` overrides it for one site. */
export type DataForSeoKeyScope = 'account' | 'site';

export interface DataForSeoKeyInput {
  login: string;
  password: string;
  scope?: DataForSeoKeyScope;
}

export interface DataForSeoKeySaved {
  configured: boolean;
  scope: DataForSeoKeyScope;
  key_hint: string;
}

export const seoApi = {
  getInsights: (siteSlug: string) =>
    api.get<ApiResponse<SeoInsightsResult>>(`/v1/sites/${siteSlug}/seo/insights`),

  refreshInsights: (siteSlug: string) =>
    api.post<ApiResponse<SeoInsightsResult>>(`/v1/sites/${siteSlug}/seo/insights/refresh`, {}),

  saveDataForSeoKey: (siteSlug: string, input: DataForSeoKeyInput) =>
    api.put<ApiResponse<DataForSeoKeySaved>>(`/v1/sites/${siteSlug}/seo/dataforseo-key`, input),

  deleteDataForSeoKey: (siteSlug: string, scope: DataForSeoKeyScope = 'account') =>
    api.delete<ApiResponse<{ configured: boolean }>>(
      `/v1/sites/${siteSlug}/seo/dataforseo-key?scope=${scope}`,
    ),
};
